import React from 'react';
import {
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ConfigGroup } from '../types/configuration';
import { COLORS_CONFIG_PAGE, StyledSelectedItem, StyledSidebar } from '../../conf/constants';
import { filterConfigGroups } from '../utils/configurationUtils';

interface ConfigSidebarProps {
  configGroups: ConfigGroup[];
  selectedItem: string;
  setSelectedItem: (item: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sidebarWidth: string;
}

const ConfigSidebar: React.FC<ConfigSidebarProps> = ({
  configGroups,
  selectedItem,
  setSelectedItem,
  searchTerm,
  setSearchTerm,
  sidebarWidth,
}) => {
  const filteredConfigGroups = filterConfigGroups(configGroups, searchTerm);

  return (
    <StyledSidebar elevation={0} sidebarWidth={sidebarWidth}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box p={2} sx={{ pb: 0 }}>
          <Typography variant="h6" gutterBottom>
            Site Configuration Entries
          </Typography>
          <TextField
            size="small"
            placeholder="Search Configuration..."
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              bgcolor: COLORS_CONFIG_PAGE.background,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                '&:focus': {
                  bgcolor: COLORS_CONFIG_PAGE.primary,
                  '& .MuiInputBase-input': {
                    color: COLORS_CONFIG_PAGE.white,
                  },
                },
              },
              borderRadius: 3,
              p: 1,
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <List component="nav" dense>
            {filteredConfigGroups.map((group) => (
              <React.Fragment key={group.name}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" color={COLORS_CONFIG_PAGE.dark} fontSize={18} fontWeight={600}>
                        {group.name}
                      </Typography>
                    }
                  />
                </ListItem>

                {group.items.map((item) =>
                  item.id === selectedItem ? (
                    <StyledSelectedItem key={item.id} onClick={() => setSelectedItem(item.id)}>
                      <ListItemText primary={item.name} secondary={item.description} />
                    </StyledSelectedItem>
                  ) : (
                    <ListItemButton key={item.id} onClick={() => setSelectedItem(item.id)}>
                      <ListItemText primary={item.name} secondary={item.description} />
                    </ListItemButton>
                  )
                )}
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Box>
    </StyledSidebar>
  );
};

export default ConfigSidebar;